// Dependencies
// -----------------------------------------------
import React from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import queryString from 'query-string';
import 'react-dates/initialize';
import ReactI18n from 'react-i18n';

// Components
// -----------------------------------------------
import { fetchWlsRoomAvailability, fetchWlsRoomPricing, fetchWlsCheckoutAvailability } from '../../utils/wls-api-client';
import {
  availabilityFromError,
  buildBookingRange,
  invalidDatesAvailability,
  isClientAvailabilityError,
  isDisplayableDateRangeError,
  validateDateRange
} from '../../utils/wls-booking-range';
import Meta from './meta';
import {
  Amenities,
  Availability,
  BookingAnchored,
  BookingToggle,
  Contact,
  Header,
  Images,
  Location,
  Navbar,
  Overview,
  Owner,
  ReviewList,
  Rules,
  Summary
} from './single/';

// -----------------------------------------------
// COMPONENT->ROOM -------------------------------
// -----------------------------------------------
class Room extends React.Component {

  // Constructor
  // ---------------------------------------------
  constructor(props, _railsContext) {
    super(props);
    this.state = {
      availability: this.props.listing.availability_calendar,
      bookingType: null,
      checkInDate: null,
      checkOutDate: null,
      bookingRange: null,
      bookingLength: 0,
      datesParsed: false,
      guests: 1,
      pricing: null,
      addonFeeIds: [],
      couponCode: '',
      room: this.props.listing.room_type_id,
      review_average: this.props.listing.review_average || 0,
      reviews: this.props.listing.reviews.length || 0
    };
  }

  // Component Did Mount
  // ---------------------------------------------
  componentDidMount() {
    this.handleBrowserState();
    window.onpopstate = this.handleBrowserState;
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) element.scrollIntoView();
    }
    window.customJavascriptLoad();
  }

  // Parse Query
  // ---------------------------------------------
  parseQuery = () => {
    const parsedQuery = queryString.parse(location.search);
    let queryInfo = {};

    //Dates
    if (parsedQuery['check-in'] && parsedQuery['check-out']) {
      queryInfo['checkIn'] = moment(parsedQuery['check-in'], 'DD-MM-YYYY');
      queryInfo['checkOut'] = moment(parsedQuery['check-out'], 'DD-MM-YYYY');

      queryInfo.bookingRangeError = validateDateRange(
        queryInfo.checkIn,
        queryInfo.checkOut
      );
      queryInfo.bookingRange = buildBookingRange(
        queryInfo.checkIn,
        queryInfo.checkOut
      );
    }

    //Num Guests
    if (parsedQuery['guests']) {
      queryInfo['guests'] = parsedQuery['guests'];
    }
    return queryInfo;
  };

  // Handle Browser State
  // ---------------------------------------------
  handleBrowserState = () => {
    const queryInfo = this.parseQuery();

    this.setState(
      {
        bookingRange: queryInfo.bookingRange || null,
        bookingLength: queryInfo.bookingRange
          ? queryInfo.bookingRange.length - 1
          : 0,
        checkInDate: queryInfo.checkIn || null,
        checkOutDate: queryInfo.checkOut || null,
        ...(isDisplayableDateRangeError(queryInfo.bookingRangeError) && {
          availability: invalidDatesAvailability(queryInfo.bookingRangeError),
          pricing: null
        }),
        guests: queryInfo.guests || 1,
        isDirty: true,
        datesParsed: true
      },
      () => {
        if (this.state.bookingRange) {
          this.checkAvailability();
        }
      }
    );
  };

  // Check Availability
  // ---------------------------------------------
  checkAvailability = () => {
    const requestedRange = this.state.bookingRange;
    const queryInfo = this.parseQuery();
    const availabilityType = this.props.listing.room_type ? 'room_type_availability' : 'availability'
    const applyAvailability = response => {
      if (this.state.bookingRange !== requestedRange) return;
      this.setState(
        { availability: response.data },
        () => {
          if (response.data.bookable) {
            this.checkPricing();
          }
        }
      );
    };

    const wlsRequest = this.props.listing.room_type
      ? fetchWlsRoomAvailability(this.props.brand, this.props.listing.id, {
          unit_id: this.props.listing.unit.id,
          booking_range: JSON.stringify(this.state.bookingRange),
          guests: queryInfo['guests']
        })
      : fetchWlsCheckoutAvailability(this.props.brand, this.props.listing.id, {
          unit_id: this.props.listing.unit.id,
          booking_range: JSON.stringify(this.state.bookingRange),
          guests: queryInfo['guests']
        });
    wlsRequest.then(applyAvailability).catch(error => {
      if (this.state.bookingRange !== requestedRange) return;
      const availability = availabilityFromError(error);
      if (availability) this.setState({ availability });
      console.log(error);
    });
  };

  // Check Pricing
  // ---------------------------------------------
  checkPricing = () => {
    const requestedRange = this.state.bookingRange;
    const queryInfo = this.parseQuery();

    fetchWlsRoomPricing(this.props.brand, this.props.listing.id, {
      booking_range: JSON.stringify(this.state.bookingRange),
      num_guests: queryInfo['guests'],
      addon_fee_ids: this.state.addonFeeIds,
      coupon_code: this.state.couponCode
    })
      .then(response => {
        if (this.state.bookingRange !== requestedRange) return;
        this.setState({ pricing: response.data });
      })
      .catch(error => { console.log(error); });
  };

  // Respond To Dates Change
  // ---------------------------------------------
  respondToDatesChange = (checkInDate, checkOutDate) => {
    const bookingRangeError = validateDateRange(checkInDate, checkOutDate);

    if (isDisplayableDateRangeError(bookingRangeError)) {
      this.setState({
        availability: invalidDatesAvailability(bookingRangeError),
        bookingType: null,
        bookingRange: null,
        bookingLength: 0,
        datesParsed: true,
        pricing: null,
        checkInDate,
        checkOutDate
      });
    } else if (!bookingRangeError) {
      const bookingRange = buildBookingRange(checkInDate, checkOutDate);
      this.setState(
        {
          availability: null,
          bookingType: null,
          bookingRange: bookingRange,
          bookingLength: bookingRange.length - 1,
          datesParsed: true,
          pricing: null,
          checkInDate: checkInDate,
          checkOutDate: checkOutDate
        },
        () => {
          if (this.state.bookingRange) {
            this.updateQueryString();
            this.checkAvailability();
          }
        }
      );
    } else if (isClientAvailabilityError(this.state.availability)) {
      // Picker is mid-selection again: drop the stale date error.
      this.setState({
        availability: null,
        bookingRange: null,
        bookingLength: 0,
        pricing: null,
        checkInDate,
        checkOutDate
      });
    }
  };

  // Get Stringified Query String
  // ---------------------------------------------
  getStringifiedQueryString = () => {
    let queryInfo = {};
    if(this.state.room){
      queryInfo['room'] = this.state.room;
    }

    if (this.state.checkInDate && this.state.checkOutDate) {
      queryInfo['check-in'] = this.state.checkInDate.format('DD-MM-YYYY');
      queryInfo['check-out'] = this.state.checkOutDate.format('DD-MM-YYYY');
    }

    if (this.state.guests) {
      queryInfo['guests'] = this.state.guests;
    }

    const stringifiedQueryString = '?' + queryString.stringify(queryInfo);
    return stringifiedQueryString;
  };

  // Respond To Guests Change
  // ---------------------------------------------
  respondToGuestsChange = guests => {
    this.setState(
      {
        availability: null,
        bookingType: null,
        pricing: null,
        guests: guests
      },
      () => {
        this.updateQueryString();
        this.checkAvailability();
      }
    );
  };

  // Update Query String
  // ---------------------------------------------
  updateQueryString = () => {
    const stringifiedQueryString = this.getStringifiedQueryString();

    history.pushState(null, null, stringifiedQueryString);
  };

  // Update Fees
  // ---------------------------------------------
  updateFees = feeId => {
    let newArray = [];

    if (this.state.addonFeeIds.includes(feeId)) {
      newArray = this.state.addonFeeIds.filter(id => id !== feeId);
    } else {
      newArray = this.state.addonFeeIds.concat(feeId);
    }
    this.setState({ addonFeeIds: newArray }, this.checkPricing);
  };

  // Add Coupon Code
  // ---------------------------------------------
  addCouponCode = code => {
    this.setState({ couponCode: code }, () => this.checkPricing());
  };

  // Render
  // ---------------------------------------------
  render() {
    return (
      <div>
        <Meta />
        <Images />
        <section className="details-main">
          <BookingAnchored
            addonFeeIds={this.state.addonFeeIds}
            availability={this.state.availability}
            checkInDate={this.state.checkInDate}
            checkOutDate={this.state.checkOutDate}
            datesParsed={this.state.datesParsed}
            guests={this.state.guests}
            pricing={this.state.pricing}
            respondToDatesChange={this.respondToDatesChange}
            respondToGuestsChange={this.respondToGuestsChange}
            updateFees={this.updateFees}
            updateQuantityFee={this.updateQuantityFees}
            addCouponCode={this.addCouponCode}
          />
          <BookingToggle
            addonFeeIds={this.state.addonFeeIds}
            availability={this.state.availability}
            checkInDate={this.state.checkInDate}
            checkOutDate={this.state.checkOutDate}
            datesParsed={this.state.datesParsed}
            guests={this.state.guests}
            pricing={this.state.pricing}
            respondToDatesChange={this.respondToDatesChange}
            respondToGuestsChange={this.respondToGuestsChange}
            updateFees={this.updateFees}
            updateQuantityFees={this.updateQuantityFees}
          />
          <figure className="details-content">
            <Navbar />
            <Header />
            <Overview />
            {/* <Amenities /> */}
            <Summary />
            <Rules pricing={this.state.pricing} />
            <div id='review-section'/>
            {this.props.property_manager && (
              <Owner />
            )}
            {this.props.listing.reviews && this.props.listing.reviews.length > 0 ? (
              <ReviewList
                displayFormat={this.props.brand.date_format}
              />
            ) : null}
            <Availability />
            <Location />
          </figure>
        </section>
      </div>
    );
  }
}

// Map State To Props
// -----------------------------------------------
function mapStateToProps(state) {
  return {
    brand: state.brand ? state.brand : {},
    listing: state.listing ? state.listing : {}
  };
}

// Export
// -----------------------------------------------
export default connect(mapStateToProps)(Room);
